import { Input, Radio, Upload, message } from "antd";
import type { UploadFile, UploadProps } from "antd";
import type { File as EntityFile, Order, ReceivePayment } from "@zhihaoscm/entity";
import { ORDER_PAY_TYPE, RECEIVE_PAYMENT_TYPE, TRADE_PRODUCT_PAY_TYPE } from "@zhihaoscm/meta";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { BaseItem, Button, Dialog, Grid, Table, type TableColumnProps } from "zhihao-ui-react";
import { fileService } from "@/api/fileService";
import { receivePaymentService } from "@/api/receivePaymentService";
import { scheduleService } from "@/api/scheduleService";

export type OrderPaymentAction = { kind: "pay" | "edit" | "view" | "confirmPay"; payment: ReceivePayment };

const acceptedVoucherTypes = [
    ".zip",
    ".rar",
    ".7z",
    ".aip",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".avif",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".pdf"
];
const money = (value?: number) =>
    Number(value ?? 0).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 旧 payDialog/offlinePaymentDialog/fileDialog 的 React 订单域适配层。 */
export function OrderPaymentDialogs({
    action,
    order,
    onClose,
    onSuccess
}: {
    action?: OrderPaymentAction;
    order: Order;
    onClose: () => void;
    onSuccess: () => Promise<void> | void;
}) {
    const payment = action?.payment;
    const editing = action?.kind === "edit";
    const viewing = action?.kind === "view";
    const defaultPayType = [
        TRADE_PRODUCT_PAY_TYPE.ONLINE.code,
        TRADE_PRODUCT_PAY_TYPE.ONLINE_AND_OFFLINE.code
    ].includes(order.productInfo?.payType)
        ? ORDER_PAY_TYPE.ONLINE.code
        : ORDER_PAY_TYPE.OFFLINE.code;
    const [payType, setPayType] = useState(defaultPayType);
    const [files, setFiles] = useState<UploadFile[]>([]);
    const [instruction, setInstruction] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [viewFiles, setViewFiles] = useState<EntityFile[]>([]);
    useEffect(() => {
        setPayType(defaultPayType);
        setFiles([]);
        setInstruction("");
        setViewFiles([]);
        if (!payment || action?.kind === "pay") return;
        void receivePaymentService.receivePaymentId(payment.id).then(async current => {
            if (viewing) {
                const ids = current.paymentFileId?.length ? current.paymentFileId : current.customFileId;
                setViewFiles(ids?.length ? await fileService.getFileByIds(ids) : []);
                return;
            }
            setInstruction(current.paymentInstruction ?? "");
            if (current.paymentFileId?.length) {
                const currentFiles = await fileService.getFileByIds(current.paymentFileId);
                setFiles(
                    currentFiles.map(file => ({
                        uid: String(file.id),
                        name: file.name,
                        status: "done",
                        url: file.path,
                        response: file
                    }))
                );
            }
        });
    }, [action, defaultPayType, payment, viewing]);
    /** 上传结果中的服务端文件 ID 是线下付款接口的唯一文件契约。 */
    const request: UploadProps["customRequest"] = async options => {
        const raw = options.file as File;
        const form = new FormData();
        form.append("file", raw);
        form.append("fileName", raw.name);
        try {
            const result = await fileService.uploadFile(form);
            options.onSuccess?.(result);
        } catch (reason) {
            options.onError?.(reason as Error);
        }
    };
    const fileIds = files
        .map(file => Number((file.response as { id?: number } | undefined)?.id ?? file.uid))
        .filter(Number.isFinite);
    /** 支付方式与旧版一致：线上获取支付地址，线下提交或更新凭证。 */
    const submit = async () => {
        if (!payment) return;
        setSubmitting(true);
        try {
            if (!editing && payType === ORDER_PAY_TYPE.ONLINE.code) {
                const result =
                    action.kind === "confirmPay" && payment.scheduleId
                        ? await scheduleService.confirmAndPay(payment.scheduleId, { receivePaymentId: payment.id })
                        : await receivePaymentService.paySuccess(payment.id);
                if (result.payUrl) window.open(result.payUrl, "_blank");
                onClose();
                return;
            }
            if (!fileIds.length) {
                void message.error("请上传支付凭证");
                return;
            }
            await (editing
                ? receivePaymentService.receivePaymentUpdatePayFile({
                      id: payment.id,
                      fileIds,
                      version: payment.version,
                      paymentInstruction: instruction
                  })
                : action.kind === "confirmPay" && payment.scheduleId
                  ? scheduleService.confirmOffLine(payment.scheduleId, {
                        receivePaymentId: payment.id,
                        fileIds,
                        paymentInstruction: instruction
                    })
                  : receivePaymentService.receivePaymentOffLinePay({
                        id: payment.id,
                        fileIds,
                        version: payment.version,
                        paymentInstruction: instruction
                    }));
            void message.success("操作成功");
            onClose();
            await onSuccess();
        } finally {
            setSubmitting(false);
        }
    };
    if (!action || !payment) return null;
    if (viewing)
        return (
            <Dialog footer={null} onClose={onClose} open title="查看凭证" width={800}>
                <Table
                    columns={[
                        {
                            type: "index",
                            label: "序号",
                            width: 60,
                            align: "center",
                        },
                        { label: "文件名称", prop: "name" },
                        { label: "文件格式", prop: "type", width: 160 },
                        {
                            label: "上传时间",
                            prop: "createdTime",
                            width: 180,
                            formatter: row => (row.createdTime ? dayjs(row.createdTime).format("YYYY-MM-DD HH:mm:ss") : "--")
                        },
                        {
                            label: "操作",
                            prop: "operation",
                            width: 100,
                            render: ({ row }) => (
                                <Button href={row.path} target="_blank" type="link">
                                    查看/下载
                                </Button>
                            )
                        }
                    ] satisfies TableColumnProps<EntityFile>[]}
                    data={viewFiles}
                    pagination={false}
                    rowKey="id"
                />
            </Dialog>
        );
    const both = order.productInfo?.payType === TRADE_PRODUCT_PAY_TYPE.ONLINE_AND_OFFLINE.code;
    return (
        <Dialog
            destroyOnClose
            footer={
                <div className="dialog-footer">
                    <Button onClick={onClose}>取消</Button>
                    <Button loading={submitting} onClick={() => void submit()} type="primary">
                        {editing || payType === ORDER_PAY_TYPE.OFFLINE.code ? "提交" : action.kind === "confirmPay" ? "确认并支付" : "支付"}
                    </Button>
                </div>
            }
            onClose={onClose}
            open
            title={editing ? "线下支付" : action.kind === "confirmPay" ? "确认并支付" : `支付${RECEIVE_PAYMENT_TYPE.getIntroByCode(payment.paymentType) ?? "费用"}`}
            width={editing || payType === ORDER_PAY_TYPE.OFFLINE.code ? 800 : 400}
        >
            <p className="order-payment-dialog-tip">
                {editing ? "为确保订单状态顺利更新，请尽快上传支付凭证" : action.kind === "confirmPay" ? "是否确认排期，确认需支付排期货款" : "请您核对支付金额"}
            </p>
            <div className="order-payment-dialog-amount">
                <span>应支付金额</span>
                <strong>¥{money(payment.amount)}</strong>
            </div>
            {both && !editing ? (
                <Radio.Group
                    onChange={event => setPayType(event.target.value)}
                    options={[
                        { label: "线上支付", value: ORDER_PAY_TYPE.ONLINE.code },
                        { label: "线下支付", value: ORDER_PAY_TYPE.OFFLINE.code }
                    ]}
                    value={payType}
                />
            ) : (
                <BaseItem
                    label="支付方式"
                    value={payType === ORDER_PAY_TYPE.ONLINE.code && !editing ? "线上支付" : "线下支付"}
                />
            )}
            {payType === ORDER_PAY_TYPE.OFFLINE.code || editing ? (
                <>
                    <h3 className="order-payment-dialog-title">线下收款账户</h3>
                    <div className="order-payment-accounts">
                        {order.productInfo?.accountInfo?.map((account, index) => (
                            <div className="order-payment-account" key={`${account.account}-${index}`}>
                                <div className="order-payment-account-number">
                                    <strong>{account.account?.replace(/.{4}(?=.)/g, "$& ")}</strong>
                                    <Button
                                        onClick={() =>
                                            void navigator.clipboard
                                                .writeText(account.account)
                                                .then(() => message.success("已复制"))
                                        }
                                        type="link"
                                    >
                                        复制
                                    </Button>
                                </div>
                                <Grid cols={2}>
                                    <BaseItem label="开户名称" value={account.name} />
                                    <BaseItem label="开户行" value={account.openingBank} />
                                </Grid>
                            </div>
                        ))}
                    </div>
                    <div className="order-payment-upload">
                        <label>
                            上传支付凭证 <small>支持最多上传10个文件，单个文件不超过50M（支持上传压缩包）</small>
                        </label>
                        <Upload
                            accept={acceptedVoucherTypes.join(",")}
                            customRequest={request}
                            fileList={files}
                            maxCount={10}
                            multiple
                            onChange={({ fileList }) => setFiles(fileList)}
                        >
                            <Button>
                                <i className="iconfont icon-upload" />
                                上传文件
                            </Button>
                        </Upload>
                    </div>
                    <label className="order-payment-instruction">
                        付款说明
                        <Input.TextArea
                            maxLength={200}
                            onChange={event => setInstruction(event.target.value)}
                            placeholder="请输入付款说明"
                            rows={3}
                            showCount
                            value={instruction}
                        />
                    </label>
                </>
            ) : null}
        </Dialog>
    );
}
